import { useCallback, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { TrashIcon, PlusIcon } from '@heroicons/react/24/outline';
import { Badge, Btn, Card } from '../../../components/admin/console';
import { AdminDeviceLookup, AdminDevicePreviewCard, AdminDeviceSearchInput } from '../../../components/admin/devices';
import { useAuth } from '../../../providers/AuthProvider';
import { api } from '../../../services/api';
import type { DeviceChannelEntry } from './data/deviceChannels';

const COHORT_TEMPLATE = `{
  "v": 1,
  "layout": "artifacts-v1",
  "variant": "platform-signed",
  "pointer": "platform-signed/manifest.json",
  "firstParty": {
    "vn": { "versionCode": 0, "versionName": "0.0.0" }
  },
  "appLibrary": {
    "com.example.app": { "versionCode": 0 }
  },
  "note": "Desk Scout — stealth cohort"
}`;

function kindTone(kind: DeviceChannelEntry['parsed']['kind']) {
  switch (kind) {
    case 'cohort':
      return 'info';
    case 'legacy':
      return 'warning';
    case 'invalid':
      return 'error';
    default:
      return 'neutral';
  }
}

export function OtaDeviceChannelsPage() {
  const { user } = useAuth();
  const isSuper = user?.role === 'super_admin';
  const qc = useQueryClient();

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['ota', 'device-channels'],
    queryFn: () => api.adminListOtaDeviceChannels(),
    enabled: isSuper,
  });

  const entries = data?.data ?? [];

  const [deviceId, setDeviceId] = useState('');
  const [bodyText, setBodyText] = useState(COHORT_TEMPLATE);
  const [formError, setFormError] = useState<string | null>(null);
  const [listFilter, setListFilter] = useState('');

  const save = useMutation({
    mutationFn: async () => {
      const id = deviceId.trim();
      if (!id) throw new Error('Device ID is required');
      let body: unknown;
      try {
        body = JSON.parse(bodyText);
      } catch {
        throw new Error('Body must be valid JSON (cohort v1 or { "legacyChannel": "beta" })');
      }
      return api.adminPutOtaDeviceChannel(id, body);
    },
    onSuccess: () => {
      setFormError(null);
      qc.invalidateQueries({ queryKey: ['ota', 'device-channels'] });
    },
    onError: (e: Error) => setFormError(e.message),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.adminDeleteOtaDeviceChannel(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ota', 'device-channels'] }),
  });

  const sorted = useMemo(() => {
    const q = listFilter.trim().toLowerCase();
    const list = [...entries].sort((a, b) => a.deviceId.localeCompare(b.deviceId));
    if (!q) return list;
    return list.filter(
      (e) =>
        e.deviceId.toLowerCase().includes(q) ||
        (e.raw ?? '').toLowerCase().includes(q),
    );
  }, [entries, listFilter]);

  const loadEntry = useCallback((e: DeviceChannelEntry) => {
    setDeviceId(e.deviceId);
    if (e.raw?.trim().startsWith('{')) {
      try {
        setBodyText(JSON.stringify(JSON.parse(e.raw), null, 2));
      } catch {
        setBodyText(e.raw ?? '');
      }
    } else {
      setBodyText(JSON.stringify({ legacyChannel: e.raw ?? 'beta' }, null, 2));
    }
    setFormError(null);
  }, []);

  if (!isSuper) {
    return (
      <div className="px-8 py-6">
        <h1 className="text-2xl font-bold text-white">Device OTA overrides</h1>
        <p className="text-sm text-slate-400 mt-2">Super admin only.</p>
      </div>
    );
  }

  return (
    <div className="px-8 py-6 space-y-6">
      <div>
        <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-cyan-400">
          Stealth cohort
        </div>
        <h1 className="text-2xl font-bold text-white mt-1">DEVICE_CHANNELS KV</h1>
        <p className="text-sm text-slate-400 mt-1 max-w-3xl">
          Per-device overrides for the OTA Worker (<code className="text-slate-300">X-Device-ID</code>
          ). JSON v1 enables immutable <code className="text-slate-300">ota-artifacts</code> manifests plus optional{' '}
          <code className="text-slate-300">firstParty</code> / <code className="text-slate-300">appLibrary</code> pins.
          Delete an entry to return the device to fleet-default routing.
        </p>
      </div>

      <Card className="p-5 space-y-4">
        <h2 className="text-sm font-semibold text-white">Add or update</h2>
        <AdminDeviceLookup
          deviceId={deviceId}
          onDeviceIdChange={setDeviceId}
        />
        <AdminDevicePreviewCard deviceId={deviceId} />
        <label className="block text-xs text-slate-400">
          JSON payload
          <textarea
            className="mt-1 w-full min-h-[220px] font-mono text-xs rounded-lg border border-slate-600 bg-slate-900 px-3 py-2 text-slate-200"
            value={bodyText}
            onChange={(ev) => setBodyText(ev.target.value)}
            spellCheck={false}
          />
        </label>
        {formError && <p className="text-sm text-red-400">{formError}</p>}
        <Btn
          variant="primary"
          disabled={save.isPending}
          onClick={() => save.mutate()}
          leftIcon={<PlusIcon className="w-4 h-4" />}
        >
          Save to KV
        </Btn>
      </Card>

      <Card padded={false} className="overflow-hidden">
        <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-3 border-b border-slate-700">
          <h2 className="text-sm font-semibold text-white">Entries ({sorted.length})</h2>
          <div className="flex items-center gap-2">
            <AdminDeviceSearchInput
              value={listFilter}
              onChange={setListFilter}
              placeholder="Filter KV keys…"
              className="relative w-56"
              aria-label="Filter KV entries"
            />
            <Btn variant="ghost" size="sm" onClick={() => refetch()} disabled={isLoading}>
              Refresh
            </Btn>
          </div>
        </div>
        {isError && (
          <p className="px-5 py-4 text-sm text-red-400">Could not load KV entries.</p>
        )}
        {!isLoading && sorted.length === 0 && (
          <p className="px-5 py-8 text-sm text-slate-500">No overrides in KV yet.</p>
        )}
        <ul className="divide-y divide-slate-700">
          {sorted.map((e) => (
            <li key={e.deviceId} className="px-5 py-3 flex flex-wrap items-start gap-3 justify-between">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono text-sm text-white">{e.deviceId}</span>
                  <Badge tone={kindTone(e.parsed.kind)}>{e.parsed.kind}</Badge>
                  {e.parsed.kind === 'legacy' && e.parsed.channel && (
                    <span className="text-xs text-slate-400">channel={e.parsed.channel}</span>
                  )}
                </div>
                {e.parsed.kind === 'invalid' && (
                  <p className="text-xs text-red-400 mt-1">{e.parsed.error}</p>
                )}
                {e.parsed.kind === 'cohort' && e.raw && (
                  <pre className="mt-2 text-[11px] text-slate-500 truncate max-w-full">{e.raw}</pre>
                )}
              </div>
              <div className="flex gap-2 shrink-0">
                <Btn variant="ghost" size="sm" onClick={() => loadEntry(e)}>
                  Edit
                </Btn>
                <Btn
                  variant="danger"
                  size="sm"
                  leftIcon={<TrashIcon className="w-4 h-4" />}
                  disabled={remove.isPending}
                  onClick={() => {
                    if (window.confirm(`Remove KV override for ${e.deviceId}?`)) {
                      remove.mutate(e.deviceId);
                    }
                  }}
                >
                  Delete
                </Btn>
              </div>
            </li>
          ))}
        </ul>
      </Card>
    </div>
  );
}

export default OtaDeviceChannelsPage;
